import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Table, { Column } from "../../components/Table";
import Button from "../../components/Button";
import { getAllPenalty, getTotalAmountPenalty } from "../../services/paymentService";

type ApiPenalty = {
  penalty_id: number;
  loan_id: number;
  schedule_id: number;
  penalty_amount: string;
  reason: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  original_penalty_amount:number | string;
  current_penalty_amount:number | string;

  loan?: {
    loan_id: number;
    borrower_id?: number;
    loan_amount?: string;
    disbursement_date?: string;
    status?: string;
    loan_code:number;
  };
  schedule?: {
    schedule_id: number;
    due_date?: string;
    principal_amount?: string;
    interest_amount?: string;
    total_due?: string;
    payment_status?: string;
  };
};

type Row = {
  id: number;
  penalty_id: number;
  loan_id: number;
  loan_code:number;
  schedule_id: number;
  penalty_amount: string;
  reason: string;
  status: string;
  created_at: string;
  loan?: ApiPenalty["loan"];
  schedule?: ApiPenalty["schedule"];
  original_penalty_amount:number | string;
  current_penalty_amount:number | string;
  mobileHeader: string;
  apiRaw?: ApiPenalty;
};

const Penalties: React.FC = () => {
  const [data, setData] = useState<Row[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPenalties, setTotalPenalties] = useState<number>(0);
  const [totalCollected, setTotalCollected] = useState<string>("0.00");
  const [fromDate, setFromDate] = useState<string>(""); // yyyy-mm-dd
  const [toDate, setToDate] = useState<string>(""); // yyyy-mm-dd
  const navigate = useNavigate();

  // load initial data (no filter)
  useEffect(() => {
    fetchPenalties();
    fetchTotalCollected();
  }, []);

  async function fetchPenalties(params?: { from_date?: string; to_date?: string; }) {
    setLoading(true);
    try {
      const res = await getAllPenalty(params);
      const rows: Row[] = (res.data ?? res?.data ?? [])?.map((p: ApiPenalty) => ({
        id: p.penalty_id,
        penalty_id: p.penalty_id,
        loan_id: p.loan_id,
        loan_code: p.loan?.loan_code,
        schedule_id: p.schedule_id,
        penalty_amount: p.penalty_amount,
        reason: p.reason ?? "-",
        status: p.status,
        created_at: p.created_at,
        loan: p.loan,
        schedule: p.schedule,
        apiRaw: p,
        original_penalty_amount: p.original_penalty_amount,
        current_penalty_amount: p.current_penalty_amount,
        mobileHeader: `Schedule ID: ${p.schedule_id} | Loan Code: ${p.loan?.loan_id ?? p.loan_id ?? "-"}`,
      }));

      setData(rows);
      const total = res.total_penalties ?? (rows.length || 0);
      setTotalPenalties(Number(total));
    } catch (err: any) {
      console.error("Failed to load penalties", err);
      toast.error(err?.message ?? "Failed to load penalties");
    } finally {
      setLoading(false);
    }
  }

  async function fetchTotalCollected(params?: { from_date?: string; to_date?: string; }) {
    try {
      const res = await getTotalAmountPenalty(params);
      const amt = res.total_collected_penalty ?? res?.total_collected_penalty ?? res?.total_collected ?? "0.00";
      setTotalCollected(String(amt));
    } catch (err: any) {
      console.error("Failed to load total collected penalty", err);
      toast.error("Failed to load total collected penalty");
    }
  }

  const formatDateDMY = (dateStr?: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr; // fallback if string isn't ISO
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  // apply filters -> send API calls with from_date/to_date
  const handleApplyFilters = async () => {
    const params: { from_date?: string; to_date?: string } = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;

    await Promise.all([fetchPenalties(params), fetchTotalCollected(params)]);
  };

  const handleResetFilters = async () => {
    setFromDate("");
    setToDate("");
    await Promise.all([fetchPenalties(), fetchTotalCollected()]);
  };

  const columns = useMemo<Column<Row>[]>(() => {
    return [
      {
        header: "",
        accessor: "mobileHeader",
        sortable: false,
        cell: (row) => (
          <div className="sm:hidden">
            <div className="font-semibold text-sm">{row.mobileHeader}</div>
          </div>
        ),
      },
      {
        header: "Loan CODE",
        accessor: "loan_code",
        sortable: true,
        cell: (row) => <span className="font-medium">{row.loan_code}</span>,
      },
      {
        header: "Amount",
        accessor: "original_penalty_amount",
        sortable: true,
        cell: (row) => (row.original_penalty_amount),
      },
      {
        header: "Reason",
        accessor: "reason",
      },
      {
        header: "Status",
        accessor: "status",
        sortable: true,
        cell: (row) => (
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.status === "Paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
            {row.status}
          </span>
        ),
      },
      {
        header: "Paid Penalty Amount",
        accessor: "current_penalty_amount",
        cell: (row) => (row?.current_penalty_amount ),
      },
      {
        header: "Schedule Due",
        accessor: "schedule.due_date",
        cell: (row) => formatDateDMY(row.schedule?.due_date ?? "-"),
      },
      {
        header: "Created_At",
        accessor: "created_at",
        // sortable: true,
        cell: (row) => formatDateDMY(row.created_at ?? "-"),
      },
    ];
  }, []);

  // actions shown in table
  const renderActions = (row: Row) => {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            navigate("/loan-dues", { state: { loan_id: row.loan_id } });
          }}
          className="px-3 py-1 rounded-md"
        >
          View Loan
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-6 pt-3">
        {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end items-start justify-between  bg-white p-4 rounded-2xl shadow-sm">
        <div>
          <h2 className="text-2xl font-semibold">Penalties</h2>
          <p className="text-sm text-slate-500">Total penalties: {totalPenalties}</p>
        </div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end items-start">
          <div className="flex flex-col">
          <label className="text-xs text-slate-600">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border px-2 py-1 rounded-md"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-slate-600">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border px-2 py-1 rounded-md"
          />
        </div>

        <div className="flex gap-2 ">
          <Button onClick={handleResetFilters} variant="secondary" className="px-4 py-2">
            Reset
          </Button>
          <Button onClick={handleApplyFilters} className="px-4 py-2">
            Apply
          </Button>
        </div>
      </div>
      </div>

      <div className="flex items-center justify-end  bg-white gap-4 rounded-2xl p-4 shadow-sm">
        

      <div>
          <div className="text-right">
          <div className="text-sm text-slate-500">Total Collected</div>
          <div className="text-xl font-bold">₹ {Number(totalCollected || "0").toFixed(2)}</div>
        </div>
      </div>
      </div>

    

      <Table<Row>
        title="Penalty Records"
        data={data}
        columns={columns}
        loading={loading}
        showActions
        renderActions={renderActions}
        defaultPageSize={10}
        pageSizeOptions={[5, 10, 25, 50]}
        mobileHeaderFields={[ "mobileHeader", "penalty_amount"]}
      />
    </div>
  );
};

export default Penalties;
